/**
 * Resolving the `team_id` deep-link param to a team the wizard can actually
 * select.
 *
 * The team list is fetched per division, so the id may simply not be in the
 * list the user ends up on. That is an ordinary outcome, not an error: the
 * caller falls back to an empty selector and the reader picks a team
 * themselves.
 */

/** The fields a match needs. Structural, so both the API `Team` and the
 * section's own team shape satisfy it. */
export interface PreselectableTeam {
  id?: string
  uid?: string
  player_count?: number | null
  max_players_per_team?: number
}

/**
 * A team is full when it has a cap and has reached it — the same condition
 * `getTeamDropdownOptions` uses to disable the option. Pre-selecting a disabled
 * option would leave the form in a state the user cannot submit and cannot see
 * the reason for.
 */
const isFull = (team: PreselectableTeam): boolean =>
  Boolean(team.max_players_per_team) &&
  (team.player_count ?? 0) >= (team.max_players_per_team as number)

/**
 * Find the team a `team_id` refers to, or null when it cannot be pre-selected.
 *
 * Matches on `uid` then `id` — the two keys the rest of the flow reads teams by
 * (see `Main.jsx`'s tracking payload and `handleTeamChange`). Deliberately NOT
 * `team_player_roster_id`: that is a roster row id, a different namespace from
 * the team id a link hands out, and matching it could select a different team
 * than the one the link named.
 */
export const findPreselectableTeam = <T extends PreselectableTeam>(
  teams: T[] | null | undefined,
  teamId: string | null | undefined
): T | null => {
  if (!teamId || !teams?.length) {
    return null
  }
  const match = teams.find((team) => team.uid === teamId || team.id === teamId)
  if (!match || isFull(match)) {
    return null
  }
  return match
}
